/**
 * Substring that ensures we don't cut a string in the middle of a unicode
 * character.
 *
 * The split characters are removed from the result. As such the
 * result may be shorter than the requested length. As a consequence,
 * safeSubstring(0,K) + safeSubstring(K) may not be equal to the original
 * string.
 *
 * Read more:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String#utf-16_characters_unicode_code_points_and_grapheme_clusters
 */
export function safeSubstring(
  str: string | undefined,
  start: number,
  end?: number
): string {
  if (!str) {
    return "";
  }

  while (isTrailingLoneSurrogate(str.charCodeAt(start))) {
    start++;
  }
  if (end === undefined) {
    end = str.length;
  }
  while (isLeadingLoneSurrogate(str.charCodeAt(end - 1))) {
    end--;
  }
  return str.substring(start, end);
}

function isLeadingLoneSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function isTrailingLoneSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}

export function pluralize(count: number) {
  return count !== 1 ? "s" : "";
}

// Encode text for use in XML element bodies and double-quoted attributes
export function escapeXml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

// Conjugates a verb based on a count, assuming it only comes down to adding an
// "s" at the end, which does not work for all words (e.g., do -> does != dos).
export function conjugate(count: number) {
  return count === 1 ? "s" : "";
}

export function sanitizeString(rawString: string) {
  return rawString.trim().toLowerCase();
}

export function slugify(text: string) {
  return text
    .normalize("NFKD") // Normalize to decomposed form.
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics.
    .replace(/([a-z])([A-Z0-9])/g, "$1_$2") // Get all lowercase letters that are near to uppercase ones and replace with _.
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_") // Replace spaces with _.
    .replace(/[\W]+/g, "_") // Replace all non-word characters with _.
    .replace(/__+/g, "_"); // Replace multiple _ with single _.
}

export function isSlugified(text: string) {
  return /^[a-z0-9_]+$/.test(text);
}

export function redactString(str: string, n: number) {
  if (typeof str !== "string") {
    return str;
  }

  if (str.length <= n) {
    return str;
  }

  const redacted = "•".repeat(str.length - n) + str.slice(-n);
  return redacted;
}

const REDACTED_SECRET_LENGTH = 8;
const SECRET_VISIBLE_SUFFIX_LENGTH = 4;
// Below this length, the visible suffix would leave too few hidden characters to resist guessing.
const SECRET_MIN_LENGTH_FOR_VISIBLE_SUFFIX = 16;

/**
 * @cc [owner:pmilliotte,label:security] secret-never-returned-verbatim
 * The returned value MUST be 8 characters whatever the secret's length, and MUST reveal nothing of a
 * secret shorter than 16 characters; a longer secret MAY keep only its last 4 characters visible.
 */
export function redactSecret(secret: string): string {
  const visibleSuffix =
    secret.length < SECRET_MIN_LENGTH_FOR_VISIBLE_SUFFIX
      ? ""
      : secret.slice(-SECRET_VISIBLE_SUFFIX_LENGTH);
  return (
    "•".repeat(REDACTED_SECRET_LENGTH - visibleSuffix.length) + visibleSuffix
  );
}

export function redactObjectValues(
  obj: Record<string, string>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, redactSecret(value)])
  );
}

const REDACTED_SECRET_PREFIX = "•••";

// Detects a value that went through `redactSecret`, e.g. a client sending back a secret it only
// ever received redacted.
export function isRedactedSecret(value: string): boolean {
  return value.startsWith(REDACTED_SECRET_PREFIX);
}

export function truncate(text: string, length: number, omission = "...") {
  return text.length > length
    ? `${text.substring(0, length - omission.length)}${omission}`
    : text;
}

export function stripNullBytes(text: string): string {
  return text.replace(/\0/g, "");
}

export function stripCRLF(text: string): string {
  return text.replace(/[\r\n]+/g, " ");
}

// Checks for an escaped null Unicode character.
export function hasNullUnicodeCharacter(text: string): boolean {
  return text.includes("\u0000");
}

const SPECIAL_CASES = {
  github: "GitHub",
  hubspot: "HubSpot",
  "stack one": "StackOne",
  "n 8n": "n8n",
  mcp: "MCP",
  webbrowser: "Web browsing",
  websearch: "Web search",
  id: "ID",
};

// Match "id" in "user id", but not inside "video". Case-insensitive so a special case still matches
// once the first letter is capitalized.
const SPECIAL_CASES_PATTERN = new RegExp(
  `\\b(?:${Object.keys(SPECIAL_CASES).join("|")})\\b`,
  "gi"
);

function isSpecialCase(s: string): s is keyof typeof SPECIAL_CASES {
  return Object.hasOwn(SPECIAL_CASES, s);
}

/**
 * @cc [owner:thomasc2a,label:product] special-cases-keep-their-casing
 * A `SPECIAL_CASES` value MUST appear exactly as written in the output, including when it starts
 * the name (e.g. `n8n` stays lowercase, it is not capitalized to `N8n`).
 */
export function formatAsDisplayName(name: string): string {
  return slugify(name)
    .replace(/_/g, " ")
    .replace(/\b\w/, (char) => char.toUpperCase())
    .replace(SPECIAL_CASES_PATTERN, (match) => {
      const key = match.toLowerCase();
      return isSpecialCase(key) ? SPECIAL_CASES[key] : match;
    });
}

export function asDisplayToolName(name?: string | null) {
  if (!name) {
    return "";
  }

  if (name === "interactive_content") {
    return "Create Frames";
  }

  if (name === "image_generation") {
    return "Create Images";
  }

  // Override the name to avoid having "Query Tables V2" show up in the UI. Ideally, we would
  // rename the tool internally to just "query_tables", but that raises more potential risks,
  // in particular with the Extension logic in extension/ui/components/actions/mcp/details/MCPActionDetails.tsx,
  // which has some hardcoded logic that could be affected.
  if (name === "query_tables_v2") {
    return "Query Tables";
  }

  if (name === "wakeups") {
    return "Wake-ups";
  }

  // Fork : nom français du serveur Google Agenda (front/lib/compta/libelles_outils.ts).
  if (name === "google_calendar") {
    return "Google Agenda";
  }

  // Fork (08/10) : « Google Drive » et non « Google drive » (serveur personnel de chaque membre).
  if (name === "google_drive") {
    return "Google Drive";
  }

  return formatAsDisplayName(name);
}

export function asDisplayName(name?: string | null) {
  if (!name) {
    return "";
  }

  return asDisplayToolName(name);
}

// Replace lone surrogates (actual Unicode surrogates, not escaped ones) with placeholder
// High surrogates: \uD800-\uDBFF
// Low surrogates: \uDC00-\uDFFF
export function toWellFormed(content: string): string {
  const placeholder = "\uFFFD";
  let result = "";

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const charCode = content.charCodeAt(i);

    // Check for high surrogate
    if (charCode >= 0xd800 && charCode <= 0xdbff) {
      // Check if it's followed by a low surrogate to form a valid pair
      if (i + 1 < content.length) {
        const nextCharCode = content.charCodeAt(i + 1);
        if (nextCharCode >= 0xdc00 && nextCharCode <= 0xdfff) {
          // Valid surrogate pair, keep both characters
          result += char + content[i + 1];
          i++; // Skip the next character as we've processed it
          continue;
        }
      }
      result += placeholder;
    } else if (charCode >= 0xdc00 && charCode <= 0xdfff) {
      result += placeholder;
    } else {
      result += char;
    }
  }

  return result;
}
